import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Check, Plus } from "lucide-react";
import { Badge } from "@/components/atoms/Badge";
import { Button } from "@/components/atoms/Button";
import { ColumnFilter } from "@/components/molecules/ColumnFilter";
import { FilterBar, FilterBarLabel, FilterScope } from "@/components/molecules/FilterBar";
import { PageHeader } from "@/components/molecules/PageHeader";
import { Pagination } from "@/components/molecules/Pagination";
import { RefreshButton } from "@/components/molecules/RefreshButton";
import { SavedFiltersButton } from "@/components/molecules/SavedFiltersButton";
import { SearchInput } from "@/components/molecules/SearchInput";
import { ScopePicker } from "@/components/molecules/ScopePicker";
import { DataTable, type Column } from "@/components/organisms/DataTable";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { fetchLabelsForScope } from "@/hooks/useScopeLabels";
import { useScopeOptions } from "@/hooks/useScopeOptions";
import { cn } from "@/lib/cn";
import { formatDateAbsolute } from "@/utils/date-helpers";
import { labelContactIds, type LabelSummary } from "@/services/labelsService";
import { usePageScope, useSettingsStore } from "@/store/settingsStore";
import { useUiStore } from "@/store/uiStore";
import { getApiErrorMessage } from "@/utils/apiErrors";
import { LabelFormDrawer } from "./LabelFormDrawer";

/** Rows per page. The list is held whole in memory, so paging is done here. */
const PAGE_SIZE = 20;

/** Branch id the create call writes for a label that spans a whole legal entity. */
const ENTITY_WIDE = "0";

const isEntityWide = (label: LabelSummary) =>
  !label.branchId || String(label.branchId) === ENTITY_WIDE;

/**
 * Whether the row's membership is known at all. The list endpoint has been seen
 * answering with and without the contacts, and "0 contacts" is a very different
 * claim from "this response didn't say".
 */
const hasMembership = (label: LabelSummary) =>
  typeof label.contactCount === "number" || label.contactIds != null || label.contacts != null;

/**
 * How many contacts the row groups. The listing's own `contactCount` is
 * authoritative — the ids it ships alongside are only what the edit form needs
 * and are not always the whole membership.
 */
const contactCount = (label: LabelSummary) =>
  typeof label.contactCount === "number" ? label.contactCount : labelContactIds(label).length;

/**
 * Checkbox list inside a column-header filter: a search box over the values in
 * the column, then the values themselves. Rendered inline rather than as a
 * nested dropdown — the panel it sits in is already portalled, and a popover
 * inside a popover fights over outside-clicks.
 */
function CheckboxFilterList({
  options,
  selected,
  onToggle,
  searchPlaceholder,
  emptyText,
}: {
  options: { value: string; label: string }[];
  selected: string[];
  onToggle: (value: string) => void;
  searchPlaceholder: string;
  emptyText: string;
}) {
  const [search, setSearch] = useState("");
  const needle = search.trim().toLowerCase();
  const visible = needle
    ? options.filter((o) => o.label.toLowerCase().includes(needle))
    : options;

  return (
    <div className="flex flex-col gap-2">
      <SearchInput value={search} onChange={setSearch} placeholder={searchPlaceholder} />
      <div className="scrollbar-metal max-h-56 overflow-y-auto">
        {visible.length === 0 && (
          <p className="px-1 py-3 text-center text-xs text-bridge-muted">{emptyText}</p>
        )}
        {visible.map((option) => {
          const checked = selected.includes(option.value);
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onToggle(option.value)}
              className={cn(
                "flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm transition-colors",
                checked ? "bg-bridge-accent/10 text-bridge-fg" : "text-bridge-fg hover:bg-bridge-bg",
              )}
            >
              <span
                className={cn(
                  "flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors",
                  checked
                    ? "border-bridge-accent bg-bridge-accent text-white"
                    : "border-bridge-border bg-white",
                )}
              >
                {checked && <Check size={12} strokeWidth={3} />}
              </span>
              <span className="truncate">{option.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Every label the signed-in user can reach in the current multi-select scope,
 * with the create and edit flows that used to sit behind "Manage Labels" on
 * the Contacts page.
 *
 * Name search goes to `GET /labels?name=`, so the list the page pages over is
 * already the server's match rather than a slice of an unfiltered fetch.
 */
export function LabelsPage() {
  const { startPageLoading, stopPageLoading } = useUiStore();
  const { entities, branches, labels: scopeLabels, loading: scopeLoading } = useScopeOptions();
  const { legalEntityId: accountLegalEntityId, branchId: accountBranchId } = useSettingsStore();
  const pageScope = usePageScope("labels");
  const clearScope = useSettingsStore((s) => s.clearScope);
  const setContactsScope = useSettingsStore((s) => s.setScope);
  const navigate = useNavigate();

  const [rows, setRows] = useState<LabelSummary[]>([]);
  const [loading, setLoading] = useState(false);

  const [nameFilter, setNameFilter] = useState<string[]>([]);
  const [branchFilter, setBranchFilter] = useState<string[]>([]);
  const [nameSearch, setNameSearch] = useState("");
  const debouncedNameSearch = useDebouncedValue(nameSearch, 300);
  const [page, setPage] = useState(0);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<LabelSummary | null>(null);

  const createDefaultScope = useMemo(() => {
    if (pageScope.branchIds.length === 1) {
      const branch = branches.find((b) => b.id === pageScope.branchIds[0]);
      return {
        legalEntityId: branch?.legalEntityId ?? accountLegalEntityId,
        branchId: pageScope.branchIds[0],
      };
    }
    if (pageScope.legalEntityIds.length === 1 && pageScope.branchIds.length === 0) {
      return { legalEntityId: pageScope.legalEntityIds[0], branchId: "" };
    }
    return { legalEntityId: accountLegalEntityId, branchId: accountBranchId ?? "" };
  }, [pageScope, branches, accountLegalEntityId, accountBranchId]);

  const load = useCallback(async () => {
    if (scopeLoading) return;
    setLoading(true);
    try {
      const all = await fetchLabelsForScope(
        pageScope,
        entities,
        branches,
        accountLegalEntityId,
        { name: debouncedNameSearch },
      );
      setRows(all);
      setPage(0);
    } catch (error) {
      setRows([]);
      toast.error(getApiErrorMessage(error) ?? "Failed to load labels. Please try again.", {
        toastId: "labels-list-error",
      });
    } finally {
      setLoading(false);
    }
  }, [scopeLoading, pageScope, entities, branches, accountLegalEntityId, debouncedNameSearch]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (loading) startPageLoading();
    else stopPageLoading();
    return () => stopPageLoading();
  }, [loading, startPageLoading, stopPageLoading]);

  const branchName = useCallback(
    (label: LabelSummary) =>
      isEntityWide(label)
        ? "All branches"
        : scopeLabels[String(label.branchId)] ?? String(label.branchId),
    [scopeLabels],
  );

  /** Options for the two column filters, taken from the rows actually loaded. */
  const nameOptions = useMemo(
    () =>
      [...new Set(rows.map((r) => r.name).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b))
        .map((name) => ({ value: name, label: name })),
    [rows],
  );

  const branchOptions = useMemo(() => {
    const byId = new Map<string, string>();
    for (const row of rows) {
      const id = isEntityWide(row) ? ENTITY_WIDE : String(row.branchId);
      if (!byId.has(id)) byId.set(id, branchName(row));
    }
    return [...byId].sort((a, b) => a[1].localeCompare(b[1])).map(([value, label]) => ({ value, label }));
  }, [rows, branchName]);

  const filtered = useMemo(() => {
    return rows.filter((row) => {
      if (nameFilter.length > 0 && !nameFilter.includes(row.name)) return false;
      if (branchFilter.length > 0) {
        const id = isEntityWide(row) ? ENTITY_WIDE : String(row.branchId);
        if (!branchFilter.includes(id)) return false;
      }
      return true;
    });
  }, [rows, nameFilter, branchFilter]);

  // A filter that shortens the list can leave the user on a page past its end.
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  useEffect(() => {
    if (page !== safePage) setPage(safePage);
  }, [page, safePage]);

  const pageRows = useMemo(
    () => filtered.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE),
    [filtered, safePage],
  );

  const toggle = (list: string[], value: string) =>
    list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (label: LabelSummary) => {
    setEditing(label);
    setFormOpen(true);
  };

  /** Contacts list for this label, scoped to the label's entity (or branch). */
  const openLabeledContacts = useCallback(
    (label: LabelSummary) => {
      const id = String(label.id ?? "").trim();
      if (!id) return;
      const entityId = (label.legalEntityId ? String(label.legalEntityId) : accountLegalEntityId)?.trim();
      const rowBranch = isEntityWide(label) ? "" : String(label.branchId ?? "").trim();
      if (entityId) {
        const labels: Record<string, string> = {
          [entityId]: scopeLabels[entityId] ?? entityId,
        };
        if (rowBranch) labels[rowBranch] = scopeLabels[rowBranch] ?? branchName(label);
        setContactsScope(
          "contacts",
          {
            legalEntityIds: rowBranch ? [] : [entityId],
            branchIds: rowBranch ? [rowBranch] : [],
          },
          labels,
        );
      }
      const params = new URLSearchParams();
      params.append("labelIds", id);
      if (label.name?.trim()) params.set("labelName", label.name.trim());
      navigate(`/contacts?${params.toString()}`);
    },
    [accountLegalEntityId, scopeLabels, branchName, setContactsScope, navigate],
  );

  const columns = useMemo<Column<LabelSummary>[]>(
    () => [
      {
        id: "name",
        header: "Name",
        width: "w-[280px]",
        className: "overflow-hidden",
        cellTooltip: (r) => r.name ?? "",
        headerAccessory: (
          <ColumnFilter
            label="Labels"
            tooltip="Show only the labels you tick."
            active={nameFilter.length > 0}
            onClear={() => setNameFilter([])}
            width={260}
          >
            <CheckboxFilterList
              options={nameOptions}
              selected={nameFilter}
              onToggle={(value) => setNameFilter((prev) => toggle(prev, value))}
              searchPlaceholder="Search labels..."
              emptyText="No labels to filter."
            />
          </ColumnFilter>
        ),
        cell: (r) => (
          <span className="block truncate text-[15px] font-semibold text-slate-800">
            {r.name || "(unnamed)"}
          </span>
        ),
      },
      {
        id: "labelDescription",
        header: "Description",
        width: "w-[280px]",
        className: "overflow-hidden",
        cellTooltip: (r) => r.labelDescription ?? "",
        cell: (r) =>
          r.labelDescription?.trim() ? (
            <span className="block truncate">{r.labelDescription}</span>
          ) : (
            <span className="text-bridge-subtle">—</span>
          ),
      },
      {
        id: "legalEntityId",
        header: "Legal Entity",
        width: "w-[240px]",
        className: "overflow-hidden",
        cellTooltip: (r) => {
          const id = r.legalEntityId ? String(r.legalEntityId) : "";
          return id ? (scopeLabels[id] ?? id) : "";
        },
        cell: (r) => {
          const id = r.legalEntityId ? String(r.legalEntityId) : "";
          if (!id) return <span className="text-bridge-subtle">—</span>;
          return <span className="block truncate">{scopeLabels[id] ?? id}</span>;
        },
      },
      {
        id: "branchId",
        header: "Branch",
        width: "w-[220px]",
        className: "overflow-hidden",
        // Entity-wide rows render a Badge, which is not a tooltip trigger — an empty
        // string keeps the cell as-is, and the badge text is short anyway.
        cellTooltip: (r) => (isEntityWide(r) ? "" : branchName(r)),
        headerAccessory: (
          <ColumnFilter
            label="Branch"
            tooltip="Show labels filed under the branches you tick."
            active={branchFilter.length > 0}
            onClear={() => setBranchFilter([])}
            width={260}
          >
            <CheckboxFilterList
              options={branchOptions}
              selected={branchFilter}
              onToggle={(value) => setBranchFilter((prev) => toggle(prev, value))}
              searchPlaceholder="Search branches..."
              emptyText="No branches to filter."
            />
          </ColumnFilter>
        ),
        cell: (r) =>
          isEntityWide(r) ? (
            <Badge tone="neutral">All branches</Badge>
          ) : (
            <span className="block truncate">{branchName(r)}</span>
          ),
      },
      {
        id: "contacts",
        header: "Contacts",
        width: "w-[100px]",
        align: "right",
        className: "overflow-hidden",
        headerTooltip: "How many contacts the label groups. Click the count to list them.",
        cell: (r) =>
          hasMembership(r) ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                openLabeledContacts(r);
              }}
              className="tabular-nums font-medium text-bridge-accent hover:underline"
              aria-label={`View contacts with label ${r.name || r.id}`}
            >
              {contactCount(r)}
            </button>
          ) : (
            <span className="text-bridge-subtle">—</span>
          ),
      },
      {
        id: "atlasCreatedAt",
        header: "Created at",
        width: "w-[200px]",
        align: "right",
        className: "overflow-hidden",
        cell: (r) => (
          <span className="block truncate text-bridge-muted">{formatDateAbsolute(r.atlasCreatedAt)}</span>
        ),
      },
      {
        id: "atlasModifiedAt",
        header: "Last Updated",
        width: "w-[200px]",
        align: "right",
        className: "overflow-hidden",
        cell: (r) => (
          <span className="block truncate text-bridge-muted">{formatDateAbsolute(r.atlasModifiedAt)}</span>
        ),
      },
    ],
    [scopeLabels, branchName, nameOptions, branchOptions, nameFilter, branchFilter, openLabeledContacts],
  );

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title="Labels"
        subtitle={`${rows.length} label${rows.length === 1 ? "" : "s"} grouping contacts for campaigns to target.`}
        actions={
          <>
            <SavedFiltersButton
              pageName="labels"
              scopePage="labels"
              captureFilterState={() => ({ nameSearch, nameFilter, branchFilter })}
              applyFilterState={(state) => {
                setNameSearch(typeof state.nameSearch === "string" ? state.nameSearch : "");
                setNameFilter(
                  Array.isArray(state.nameFilter)
                    ? state.nameFilter.filter((value): value is string => typeof value === "string")
                    : [],
                );
                setBranchFilter(
                  Array.isArray(state.branchFilter)
                    ? state.branchFilter.filter((value): value is string => typeof value === "string")
                    : [],
                );
                setPage(0);
              }}
            />
            <RefreshButton onClick={() => void load()} loading={loading} />
            <Button variant="primary" onClick={openCreate}>
              <Plus size={16} />
              New Label
            </Button>
          </>
        }
      />

      <FilterScope
        onClear={() => {
          clearScope("labels");
          setNameFilter([]);
          setBranchFilter([]);
          setNameSearch("");
        }}
      >
        <div className="glass mx-6 mb-6 flex flex-1 flex-col overflow-hidden rounded-lg">
          <FilterBar className="bg-bridge-background">
            <FilterBarLabel className="min-w-[80px]" />
            <ScopePicker
              page="labels"
              tooltip="Limit the list to specific legal entities or branches. Nothing ticked shows every label you can see."
            />
            <div className="min-w-[220px] max-w-[280px]">
              <SearchInput
                value={nameSearch}
                onChange={setNameSearch}
                placeholder="Search labels by name..."
              />
            </div>
          </FilterBar>

          <div className="scrollbar-metal flex-1 overflow-auto">
            <DataTable
              rows={pageRows}
              columns={columns}
              loading={loading}
              rowKey={(r) => String(r.id)}
              onRowClick={openEdit}
              markVisitedRow
              customization={{
                tableId: "labels",
                lockedColumnIds: ["name"],
                pinnedStartColumnIds: ["name"],
              }}
              emptyTitle={
                !scopeLoading && !accountLegalEntityId && entities.length === 0
                  ? "Select a legal entity"
                  : "No labels match"
              }
              emptyDescription={
                !scopeLoading && !accountLegalEntityId && entities.length === 0
                  ? "Pick an organisation in the header to list its labels."
                  : "Create a label to group contacts a campaign can target."
              }
            />
          </div>

          <Pagination
            page={safePage}
            totalPages={filtered.length === 0 ? 0 : totalPages}
            totalElements={filtered.length}
            size={PAGE_SIZE}
            onPageChange={setPage}
          />
        </div>
      </FilterScope>

      <LabelFormDrawer
        open={formOpen}
        label={editing}
        defaultCreateScope={createDefaultScope}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        onSaved={() => void load()}
      />
    </div>
  );
}
